export const getRegistrationsSchema = {
  querystring: {
    type: "object",
    properties: {
      eventId: { type: "number" },
      page: { type: "number" },
      limit: { type: "number" },
      sort: { type: "string" },
    },
    required: ["eventId"],
  },
};

export const getRegistrationByIdSchema = {
  params: {
    type: "object",
    properties: {
      id: { type: "number" },
    },
    required: ["id"],
  },
};

export const getRegistrationOverviewSchema = {
  querystring: {
    type: "object",
    properties: {
      eventId: { type: "number" },
    },
    required: ["eventId"],
  },
};

export const getRegistrationEventSchema = {
  querystring: {
    type: "object",
    properties: {
      eventSlug: { type: "string", minLength: 1 },
    },
    required: ["eventSlug"],
  },
};

export const createRegistrationSchema = {
  body: {
    type: "object",
    properties: {
      eventId: { type: "number" },
      companyName: { type: "string" },
      email: { type: "string" },
      vatNumber: { type: "string" },
      firstName: { type: "string" },
      lastName: { type: "string" },
      type: { type: "string", enum: ["individual", "company"] },
      additionalFields: { type: "object" },
      inviteToken: { type: "string" },
      tickets: {
        type: "array",
        items: {
          type: "object",
          properties: {
            firstName: { type: "string" },
            lastName: { type: "string" },
            email: { type: "string" },
            ticketTypeId: { type: ["number", "null"] },
            additionalFields: { type: "object" },
          },
          required: ["firstName", "lastName", "email"],
        },
      },
      shouldApprove: { type: "boolean" },
    },
    anyOf: [
      {
        properties: {
          type: { const: "individual" },
        },
        required: ["firstName", "lastName"],
      },
      {
        properties: {
          type: { const: "company" },
        },
        required: ["companyName"],
      },
    ],
    required: ["eventId", "email", "type", "tickets"],
  },
};

export const patchRegistrationSchema = {
  body: {
    type: "object",
    properties: {
      id: { type: "number" },
      status: {
        type: "string",
        enum: ["approved", "rejected", "invoiced"],
      },
      companyName: { type: ["string"] },
      firstName: { type: ["string", "null"] },
      lastName: { type: ["string", "null"] },
      email: { type: ["string"] },
      vatNumber: { type: ["string", "null"] },
      additionalFields: { type: "object" },
    },
    required: ["id"],
    additionalProperties: false,
  },
};

export const patchTicketsSchema = {
  body: {
    type: "object",
    properties: {
      registrationId: { type: "number" },
      tickets: {
        type: "array",
        items: {
          type: "object",
          properties: {
            id: { type: ["number", "null"] },
            firstName: { type: "string" },
            lastName: { type: "string" },
            email: { type: "string" },
            ticketTypeId: { type: ["number", "null"] },
            additionalFields: { type: "object" },
          },
          required: ["id", "firstName", "lastName", "email"],
        },
      },
    },
    required: ["registrationId", "tickets"],
    additionalProperties: false,
  },
};

export const createRegistrationExportSchema = {
  body: {
    type: "object",
    properties: {
      eventId: { type: "number" },
      format: { type: "string", enum: ["csv", "xlsx"] },
      statuses: {
        type: "array",
        items: {
          type: "string",
          enum: ["approved", "pending", "rejected", "invoiced"],
        },
      },
      groupBy: {
        type: "string",
        enum: ["registrations", "tickets"],
      },
      timeFrame: {
        type: "string",
        enum: ["all", "24h", "7d", "30d"],
      },
    },
    required: ["eventId"],
  },
};

export const getRegistrationSettingsSchema = {
  query: {
    type: "object",
    required: ["eventId"],
    properties: {
      eventId: { type: "integer" },
    },
  },
};

export const registrationSettingsSchema = {
  params: {
    type: "object",
    properties: {
      eventId: { type: "integer" },
    },
    required: ["eventId"],
  },
  body: {
    type: "object",
    properties: {
      registrationsOpenDate: {
        type: ["string", "null"],
        format: "iso-date-time",
      },
      registrationsCloseDate: {
        type: ["string", "null"],
        format: "iso-date-time",
      },
      maxQuantity: { type: "integer", minimum: 1 },
      maxCapacity: { type: ["integer", "null"], minimum: 1 },
      color: {
        type: ["string", "null"],
        pattern: "^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$",
      },
      displaySponsors: { type: ["boolean", "null"] },
      sendSubmissionCopyEmail: { type: ["boolean", "null"] },
      submissionCopyRecipients: {
        type: "array",
        items: { type: "string", enum: ["registration", "attendee"] },
        uniqueItems: true,
      },
      customTextI18n: {
        type: ["object", "null"],
        additionalProperties: true,
      },
    },
  },
};

export const web_registrationSettingsSchema = {
  type: "object",
  properties: {
    registrationsOpenDate: {
      type: ["string", "null"],
    },
    registrationsCloseDate: {
      type: ["string", "null"],
    },
    maxQuantity: { type: "integer", minimum: 1 },
    maxCapacity: { type: ["integer", "null"], minimum: 1 },
    color: {
      type: ["string", "null"],
      pattern: "^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$",
    },
    displaySponsors: { type: ["boolean", "null"] },
    sendSubmissionCopyEmail: { type: ["boolean", "null"] },
    submissionCopyRecipients: {
      type: "array",
      items: { type: "string", enum: ["registration", "attendee"] },
      uniqueItems: true,
    },
    customTextI18n: {
      type: ["object", "null"],
      additionalProperties: true,
    },
  },
};

export const getRegistrationHistorySchema = {
  querystring: {
    type: "object",
    properties: {
      eventId: { type: "number" },
      page: { type: "number" },
      limit: { type: "number" },
      action: {
        type: "string",
        enum: [
          "created",
          "updated",
          "deleted",
          "approved",
          "rejected",
          "invoiced",
          "email_sent",
          "settings_updated",
        ],
      },
      user: { type: "string" },
    },
    required: ["eventId"],
  },
};

function getFieldType(fieldType) {
  switch (fieldType) {
    case "text":
    case "country":
    case "date":
    case "select":
      return "string";
    case "boolean":
      return "boolean";
    case "multiselect":
      return "array";
    default:
      return "string";
  }
}

function buildAdditionalFieldsProperties(customFields, options = {}) {
  const { enforceRequired = false, includeErrorMessages = false } = options;
  const properties = {};

  customFields.forEach((field) => {
    const fieldType = getFieldType(field.type);

    if (enforceRequired && field.required) {
      if (fieldType === "string") {
        properties[field.key] = {
          type: "string",
          minLength: 1,
          ...(includeErrorMessages && {
            errorMessage: `${field.label} is required`,
          }),
        };
      } else if (fieldType === "array") {
        properties[field.key] = {
          type: "array",
          minItems: 1,
          ...(includeErrorMessages && {
            errorMessage: `${field.label} is required`,
          }),
        };
      } else if (fieldType === "boolean") {
        properties[field.key] = { type: "boolean" };
      } else {
        properties[field.key] = {
          type: fieldType,
          ...(includeErrorMessages && {
            errorMessage: `${field.label} is required`,
          }),
        };
      }
    } else {
      properties[field.key] = { type: [fieldType, "null"] };
    }
  });

  return properties;
}

function getRequiredFieldKeys(customFields) {
  return customFields.filter((f) => f.required).map((f) => f.key);
}

function getEmailSchema(options = {}) {
  const { includeErrorMessages = false } = options;
  const schema = {
    type: "string",
    minLength: 1,
    pattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
  };

  if (includeErrorMessages) {
    schema.errorMessage = {
      pattern: "Invalid email format",
      minLength: "Email is required",
      type: "Email is required",
    };
  } else {
    schema.errorMessage = "Invalid email format";
  }

  return schema;
}

function getTypeConditionalSchema(options = {}) {
  const { enforceMinLength = false } = options;

  return [
    {
      if: {
        properties: { type: { const: "individual" } },
      },
      then: {
        required: ["firstName", "lastName"],
        properties: {
          firstName: enforceMinLength
            ? { type: "string", minLength: 1 }
            : { type: "string" },
          lastName: enforceMinLength
            ? { type: "string", minLength: 1 }
            : { type: "string" },
        },
      },
    },
    {
      if: {
        properties: { type: { const: "company" } },
      },
      then: {
        required: ["companyName"],
        properties: {
          companyName: enforceMinLength
            ? { type: "string", minLength: 1 }
            : { type: "string" },
        },
      },
    },
  ];
}

function buildTicketSchema(ticketCustomFields, options = {}) {
  const { includeId = false, requireTicketType = false } = options;

  const properties = {
    firstName: { type: "string", minLength: 1 },
    lastName: { type: "string", minLength: 1 },
    email: getEmailSchema(),
    ticketTypeId: requireTicketType
      ? { type: "string", minLength: 1 }
      : { type: ["string", "null"] },
    additionalFields: {
      type: "object",
      properties: buildAdditionalFieldsProperties(ticketCustomFields, {
        enforceRequired: true,
      }),
      required: getRequiredFieldKeys(ticketCustomFields),
      additionalProperties: false,
    },
  };

  if (includeId) {
    properties.id = { type: ["number", "null"] };
  }

  return {
    type: "object",
    properties,
    required: requireTicketType
      ? ["firstName", "lastName", "email", "ticketTypeId"]
      : ["firstName", "lastName", "email"],
    additionalProperties: true,
  };
}

export const web_createRegistrationSchema = (
  customFields = [],
  options = {}
) => {
  const { requireTicketType = false } = options;

  const registrationCustomFields = customFields.filter(
    (f) => f.appliedTo === "registrations"
  );
  const ticketCustomFields = customFields.filter(
    (f) => f.appliedTo === "ticket"
  );

  return {
    type: "object",
    properties: {
      eventId: { type: "number" },
      type: { type: "string", enum: ["individual", "company"] },
      email: getEmailSchema(),
      firstName: { type: ["string", "null"] },
      lastName: { type: ["string", "null"] },
      companyName: { type: ["string", "null"] },
      vatNumber: { type: ["string", "null"] },
      additionalFields: {
        type: "object",
        properties: buildAdditionalFieldsProperties(registrationCustomFields, {
          enforceRequired: true,
        }),
        required: getRequiredFieldKeys(registrationCustomFields),
        additionalProperties: false,
      },
      tickets: {
        type: "array",
        minItems: 1,
        items: buildTicketSchema(ticketCustomFields, { requireTicketType }),
      },
    },
    allOf: getTypeConditionalSchema({ enforceMinLength: true }),
    required: ["eventId", "email", "type", "tickets"],
    additionalProperties: false,
  };
};

export const web_updateRegistrationSchema = (customFields = []) => {
  const registrationCustomFields = customFields.filter(
    (f) => f.appliedTo === "registrations"
  );

  return {
    type: "object",
    properties: {
      id: { type: "number" },
      type: { type: "string", enum: ["individual", "company"] },
      email: getEmailSchema({ includeErrorMessages: true }),
      firstName: { type: ["string", "null"] },
      lastName: { type: ["string", "null"] },
      companyName: { type: ["string", "null"] },
      vatNumber: { type: ["string", "null"] },
      additionalFields: {
        type: "object",
        properties: buildAdditionalFieldsProperties(registrationCustomFields, {
          enforceRequired: true,
          includeErrorMessages: true,
        }),
        required: getRequiredFieldKeys(registrationCustomFields),
        additionalProperties: false,
      },
    },
    allOf: getTypeConditionalSchema({ enforceMinLength: true }),
    required: ["id", "email", "type"],
    additionalProperties: false,
  };
};

export const web_createTicketValidationSchema = (customFieldDefinitions) => {
  const ticketCustomFields = customFieldDefinitions || [];

  return {
    type: "object",
    properties: {
      registrationId: { type: "number" },
      tickets: {
        type: "array",
        minItems: 1,
        items: buildTicketSchema(ticketCustomFields, {
          includeId: true,
          enforceRequired: true,
        }),
      },
    },
    required: ["registrationId", "tickets"],
    additionalProperties: true,
  };
};

export const orderRegistrationCustomFieldsSchema = {
  body: {
    type: "object",
    properties: {
      eventId: { type: "integer" },
      appliedTo: {
        type: "string",
        enum: ["registrations", "ticket"],
      },
      updates: {
        type: "array",
        items: {
          type: "object",
          properties: {
            id: { type: "integer" },
            order: { type: "integer", minimum: 1 },
          },
          required: ["id", "order"],
        },
        minItems: 1,
      },
    },
    required: ["eventId", "appliedTo", "updates"],
    additionalProperties: false,
  },
};

export const HISTORY_ACTIONS = {
  CREATED: "created",
  UPDATED: "updated",
  DELETED: "deleted",
  APPROVED: "approved",
  REJECTED: "rejected",
  INVOICED: "invoiced",
  EMAIL_SENT: "email_sent",
  SETTINGS_UPDATED: "settings_updated",
};
